import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Search, Bell, Home as HomeIcon, Tv, Film, Star, Menu, X, Grid, Crown } from "lucide-react";
import { NotificationBell } from "../notifications";
import { LanguageSwitcher } from "../layout";
import { UserMenu } from "../auth";

const Navbar = ({ scrolled, onSearchClick, onSignUpClick, onOpenAuth }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  const isActive = (path) => location.pathname === path;
  
  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-black/95' : 'bg-gradient-to-b from-black/90 to-transparent'}`} data-testid="navbar">
      <div className="flex items-center justify-between px-4 md:px-8 py-3">
        <div className="flex items-center gap-6">
          <div className="cursor-pointer" onClick={() => navigate('/')} data-testid="logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_streamking-cbx/artifacts/exnqqglo_cbxtransp.png" 
              alt="CBXTV" 
              className="h-[126px] md:h-[154px] w-auto object-contain"
              style={{ filter: 'drop-shadow(0 0 3px white) drop-shadow(0 0 6px rgba(255,255,255,0.6))' }}
            />
          </div>
          
          <div className="hidden md:flex items-center gap-1">
            <NavButton icon={<HomeIcon size={16} />} text="Home" active={isActive('/')} onClick={() => navigate('/')} />
            <NavButton icon={<Tv size={16} />} text="Live TV" active={isActive('/live-tv')} onClick={() => navigate('/live-tv')} />
            <NavButton icon={<Grid size={16} />} text="Guide" active={isActive('/guide')} onClick={() => navigate('/guide')} />
            <NavButton icon={<Film size={16} />} text="Library" active={isActive('/library') || isActive('/movies')} onClick={() => navigate('/library')} />
            <NavButton text="Free TV" active={isActive('/free-movies')} onClick={() => navigate('/free-movies')} />
            <NavButton icon={<Star size={16} />} text="CBXtra" highlight active={isActive('/cbxtra')} onClick={() => navigate('/cbxtra')} />
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <button onClick={onSearchClick} className="p-2 text-white hover:text-red-500 transition-colors" data-testid="search-btn">
            <Search size={20} />
          </button>
          {/* Premium Subscribe Button */}
          <button 
            onClick={() => navigate('/subscription')}
            className="hidden md:flex items-center gap-1 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-black font-bold px-3 py-1.5 rounded-lg text-sm"
          >
            <Crown size={16} />
            Premium
          </button>
          <button 
            onClick={onSignUpClick}
            className="hidden md:block p-2 text-white hover:text-red-500 transition-colors"
            title="Subscribe for Updates"
          >
            <Bell size={20} />
          </button>
          {/* Language Switcher */}
          <div className="hidden md:block">
            <LanguageSwitcher />
          </div>
          {/* Notification Bell for logged-in users */}
          <NotificationBell />
          {/* User Menu with Auth */}
          <UserMenu onOpenAuth={onOpenAuth} />
          <button className="md:hidden text-white p-2" onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
      
      {menuOpen && (
        <div className="md:hidden bg-black/95 px-4 py-4 space-y-2 border-t border-gray-800">
          <MobileNavButton icon={<HomeIcon size={18} />} text="Home" onClick={() => { navigate('/'); setMenuOpen(false); }} />
          <MobileNavButton icon={<Tv size={18} />} text="Live TV" onClick={() => { navigate('/live-tv'); setMenuOpen(false); }} />
          <MobileNavButton icon={<Grid size={18} />} text="Guide" onClick={() => { navigate('/guide'); setMenuOpen(false); }} />
          <MobileNavButton icon={<Film size={18} />} text="Library" onClick={() => { navigate('/library'); setMenuOpen(false); }} />
          <MobileNavButton text="Free TV" onClick={() => { navigate('/free-movies'); setMenuOpen(false); }} />
          <MobileNavButton icon={<Star size={18} />} text="CBXtra" highlight onClick={() => { navigate('/cbxtra'); setMenuOpen(false); }} />
          <div className="pt-2 border-t border-gray-800">
            <div className="mb-3 px-4">
              <p className="text-gray-500 text-xs mb-2">Language</p>
              <LanguageSwitcher variant="inline" />
            </div>
            <button 
              onClick={() => { onSignUpClick && onSignUpClick(); setMenuOpen(false); }}
              className="w-full flex items-center gap-2 px-4 py-3 text-white bg-red-600 hover:bg-red-700 rounded-lg font-medium"
            >
              <Bell size={18} />
              Sign Up for Notifications
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

const NavButton = ({ icon, text, active, highlight, onClick }) => (
  <button 
    onClick={onClick}
    className={`flex items-center gap-2 px-3 py-2 rounded text-sm font-medium transition-all ${
      active ? 'bg-white/10 text-white' : 'text-gray-300 hover:text-white hover:bg-white/5'
    } ${highlight && !active ? 'text-red-500' : ''}`}
  >
    {icon}
    {text}
  </button>
);

const MobileNavButton = ({ icon, text, highlight, onClick }) => (
  <button 
    onClick={onClick}
    className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg text-left transition-colors ${
      highlight ? 'text-red-500 bg-red-500/10' : 'text-white hover:bg-white/10'
    }`}
  >
    {icon}
    <span className="font-medium">{text}</span>
  </button>
);

export default Navbar;
